/**
 * Admin User Statistics Section
 *
 * Main component that composes all user statistics cards and trend charts.
 * Replaces the KB build history in the Cost & Usage tab.
 */

import { useState } from 'react';
import { useUserStatsSummary, useUserTrends } from '../../hooks/useUserStatistics';
import { UserActivityTrendChart } from './UserActivityTrendChart';
import {
  GroupStatsCard,
  ActiveUsersCard,
  WatchlistStatsCard,
  SessionStatsCard,
  TopCompaniesCard,
  TopQuestionsCard,
  ChatStatsCard,
} from './UserStatsCards';

const PERIOD_OPTIONS = [
  { label: '7 Days', value: 7 },
  { label: '30 Days', value: 30 },
  { label: '90 Days', value: 90 },
];

export function AdminUserStatsSection() {
  const [periodDays, setPeriodDays] = useState(30);
  const [trendDays, setTrendDays] = useState(30);

  // Fetch summary data
  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useUserStatsSummary(periodDays);

  // Fetch trend data
  const {
    data: trends,
    isLoading: trendsLoading,
  } = useUserTrends(trendDays);

  return (
    <div className="space-y-6">
      {/* Header with period selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">User Statistics</h2>
          <p className="text-sm text-gray-500 mt-1">
            Activity and engagement metrics for your organization
          </p>
        </div>

        {/* Period selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Period:</span>
          <select
            value={periodDays}
            onChange={(e) => setPeriodDays(Number(e.target.value))}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {PERIOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error state */}
      {summaryError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-700">
            Failed to load user statistics: {summaryError.message}
          </p>
        </div>
      )}

      {/* Active User Trends Chart */}
      <UserActivityTrendChart
        data={trends || []}
        isLoading={trendsLoading}
        selectedPeriod={trendDays}
        onPeriodChange={setTrendDays}
      />

      {/* Main stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Group Statistics */}
        <GroupStatsCard
          data={summary?.groups}
          totalGroups={summary?.total_groups || 0}
          avgMembers={summary?.avg_members_per_group || 0}
          isLoading={summaryLoading}
          error={summaryError}
        />

        {/* Session Statistics */}
        <SessionStatsCard
          data={summary?.session_stats}
          isLoading={summaryLoading}
          error={summaryError}
        />

        {/* Watchlist Statistics */}
        <WatchlistStatsCard
          data={summary?.watchlist_stats}
          isLoading={summaryLoading}
          error={summaryError}
        />
      </div>

      {/* Chat Stats - Full Width */}
      <ChatStatsCard
        totalConversations={summary?.total_conversations || 0}
        totalMessages={summary?.total_messages || 0}
        avgMessagesPerConversation={summary?.avg_messages_per_conversation || 0}
        isLoading={summaryLoading}
      />

      {/* Secondary stats grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Most Active Users */}
        <ActiveUsersCard
          data={summary?.most_active_users}
          isLoading={summaryLoading}
          error={summaryError}
        />

        {/* Top Viewed Companies */}
        <TopCompaniesCard
          data={summary?.top_companies}
          isLoading={summaryLoading}
          error={summaryError}
        />
      </div>

      {/* Top Questions - Full Width */}
      <TopQuestionsCard
        data={summary?.top_questions}
        isLoading={summaryLoading}
        error={summaryError}
      />
    </div>
  );
}

export default AdminUserStatsSection;
